import React, { useEffect, useState, useCallback } from "react";
import {
  createOrUpdateNewBucketAccessLists,
  deleteBucketAccessLists,
  fetchAllBucketAccessLists,
  fetchBucketAccessBucketsList,
} from "../api";
import {
  Alert,
  Autocomplete,
  Box,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  IconButton,
  InputAdornment,
  Paper,
  Snackbar,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
  Chip,
  Skeleton,
  TablePagination,
} from "@mui/material";
import EditIcon from "@mui/icons-material/Edit";
import AddCircleIcon from "@mui/icons-material/AddCircle";
import DeleteIcon from "@mui/icons-material/Delete";
import SearchIcon from "@mui/icons-material/Search";
import StorageIcon from "@mui/icons-material/Storage";
import ListAltIcon from "@mui/icons-material/ListAlt";
import CloseIcon from "@mui/icons-material/Close";
import SaveIcon from "@mui/icons-material/Save";
import KeyIcon from "@mui/icons-material/Key";
import { Button } from "react-bootstrap";
import { getErrorMessage } from "../utils";
import DeleteConfirmation from "./DeleteConfirmation";

export default function BucketAccessListsCRUD({ admin }) {
  const [data, setData] = useState([]);
  const [filteredData, setFilteredData] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [isErrorData, setIsErrorData] = useState(false);
  const [isLoadingData, setIsLoadingData] = useState(true);
  const [isPendingData, setIsPendingData] = useState(false);
  const [validationErrors, setValidationErrors] = useState({});
  const [bannerError, setBannerError] = useState("");
  const [toast, setToast] = useState({ open: false, severity: "info", message: "" });
  const [deleteTargetRow, setDeleteTargetRow] = useState(null);
  const [bucketsList, setBucketsList] = useState([]);
  const [editDialog, setEditDialog] = useState({ open: false, mode: "create", data: null });
  const [formValues, setFormValues] = useState({ bucket_name: "", access_lists: "" });

  // Paging state
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);

  // Paging handlers
  const handleChangePage = (event, newPage) => setPage(newPage);
  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  // Slice filteredData for current page
  const paginatedData = filteredData.slice(
    page * rowsPerPage,
    page * rowsPerPage + rowsPerPage
  );

  // Reset page to 0 when filteredData changes
  useEffect(() => {
    setPage(0);
  }, [filteredData]);

  const showToast = (severity, message) => setToast({ open: true, severity, message });
  const closeToast = () => setToast((t) => ({ ...t, open: false }));

  useEffect(() => {
    if (!searchTerm.trim()) {
      setFilteredData(data);
    } else {
      const lower = searchTerm.toLowerCase();
      setFilteredData(
        data.filter(
          (item) =>
            item.bucket_name?.toLowerCase().includes(lower) ||
            item.access_lists?.toLowerCase().includes(lower)
        )
      );
    }
  }, [searchTerm, data]);

  function validateData(values) {
    const errors = {};
    if (!values?.bucket_name?.trim()) {
      errors.bucket_name = "Bucket Name is required";
    } else if (editDialog.mode === "create" && !bucketsList.includes(values.bucket_name)) {
      errors.bucket_name = "Please select a valid bucket from the list";
    }
    if (!values?.access_lists?.trim()) errors.access_lists = "Access List(s) is/are required";
    return errors;
  }

  const fetchRecordsData = useCallback(async () => {
    setIsErrorData(false);
    setBannerError("");
    try {
      const response = await fetchAllBucketAccessLists();
      setData(response?.data ?? []);
    } catch (error) {
      setIsErrorData(true);
      setData([]);
      const msg = getErrorMessage(error, "Error loading data");
      setBannerError(msg);
      showToast("error", msg);
    } finally {
      setIsLoadingData(false);
    }
  }, []);

  const fetchBucketsLists = useCallback(async () => {
    try {
      const response = await fetchBucketAccessBucketsList();
      setBucketsList(response?.data ?? []);
    } catch (error) {
      setIsErrorData(true);
      setBucketsList([]);
      const msg = getErrorMessage(error, "Error loading buckets list");
      setBannerError(msg);
      showToast("error", msg);
    }
  }, []);

  useEffect(() => {
    fetchRecordsData();
    fetchBucketsLists();
  }, [fetchRecordsData, fetchBucketsLists]);

  const openCreateDialog = () => {
    setFormValues({ bucket_name: "", access_lists: "" });
    setValidationErrors({});
    setBannerError("");
    setEditDialog({ open: true, mode: "create", data: null });
  };

  const openEditDialog = (item) => {
    setFormValues({ ...item });
    setValidationErrors({});
    setBannerError("");
    setEditDialog({ open: true, mode: "edit", data: item });
  };

  const closeDialog = () => {
    setEditDialog({ open: false, mode: "create", data: null });
    setValidationErrors({});
  };

  const handleFormChange = (field, value) => {
    setFormValues((prev) => ({ ...prev, [field]: value }));
    if (validationErrors[field]) {
      setValidationErrors((prev) => ({ ...prev, [field]: undefined }));
    }
  };

  const handleSave = async () => {
    const errors = validateData(formValues);
    if (Object.values(errors).some(Boolean)) {
      setValidationErrors(errors);
      showToast("error", "Please fix the highlighted fields.");
      return;
    }
    setIsPendingData(true);
    setBannerError("");
    try {
      await createOrUpdateNewBucketAccessLists(formValues);
      await fetchRecordsData();
      await fetchBucketsLists();
      closeDialog();
      showToast("success", editDialog.mode === "create" ? "Bucket access list created successfully." : "Bucket access list updated successfully.");
    } catch (error) {
      const msg = getErrorMessage(error);
      setBannerError(msg);
      showToast("error", msg);
    } finally {
      setIsPendingData(false);
    }
  };

  const requestDelete = (item) => {
    setBannerError("");
    setDeleteTargetRow(item);
  };

  const handleCancelDelete = () => setDeleteTargetRow(null);

  const handleConfirmDelete = async () => {
    if (!deleteTargetRow) return;
    try {
      await deleteBucketAccessLists(deleteTargetRow.id);
      await fetchRecordsData();
      showToast("success", "Bucket access list deleted successfully.");
    } catch (error) {
      const msg = getErrorMessage(error);
      setBannerError(msg);
      showToast("error", msg);
    } finally {
      setDeleteTargetRow(null);
    }
  };

  const getAvailableBuckets = () => {
    const currentValue = formValues.bucket_name;
    const options = bucketsList.map((bucket) => bucket);
    if (currentValue && !bucketsList.includes(currentValue) && editDialog.mode === "edit") {
      return [currentValue, ...options];
    }
    return options;
  };

  const TableSkeleton = () => (
    <TableBody>
      {[1, 2, 3, 4].map((i) => (
        <TableRow key={i}>
          <TableCell><Skeleton variant="text" width={120} /></TableCell>
          <TableCell><Skeleton variant="text" width="80%" /></TableCell>
          {admin && <TableCell><Skeleton variant="text" width={80} /></TableCell>}
        </TableRow>
      ))}
    </TableBody>
  );

  const EmptyState = () => (
    <TableRow>
      <TableCell colSpan={admin ? 3 : 2} sx={{ py: 8, textAlign: "center" }}>
        <ListAltIcon sx={{ fontSize: 48, color: "#bdbdbd", mb: 1 }} />
        <Typography variant="h6" color="text.secondary" gutterBottom>
          No Bucket Access Lists Found
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          {searchTerm ? "No results match your search criteria." : "Get started by creating your first bucket access list."}
        </Typography>
        {admin && !searchTerm && (
          <Button variant="primary" onClick={openCreateDialog}>
            <AddCircleIcon fontSize="small" style={{ marginRight: 8 }} />
            Create Bucket Access List
          </Button>
        )}
      </TableCell>
    </TableRow>
  );

  return (
    <Box sx={{ maxWidth: 1400, mx: "auto", p: 3 }}>
      {/* Header */}
      <Paper
        elevation={0}
        sx={{
          p: 3,
          mb: 3,
          borderRadius: 3,
          background: "linear-gradient(135deg, #496ab2 0%, #3f51b5 100%)",
          color: "white",
        }}
      >
        <Stack direction={{ xs: "column", sm: "row" }} justifyContent="space-between" alignItems={{ xs: "stretch", sm: "center" }} spacing={2}>
          <Box>
            <Stack direction="row" alignItems="center" spacing={1}>
              <KeyIcon sx={{ fontSize: 28, color: "#ffd700" }} />
              <Typography variant="h5" fontWeight={700}>
                Bucket Access Lists Management
              </Typography>
            </Stack>
            <Typography variant="body2" sx={{ opacity: 0.9 }}>
              Manage access control lists for the storage buckets
            </Typography>
          </Box>
          {admin && (
            <Button variant="light" onClick={openCreateDialog} style={{ display: "flex", alignItems: "center", gap: 8, fontWeight: 600 }}>
              <AddCircleIcon fontSize="small" />
              Create New
            </Button>
          )}
        </Stack>
      </Paper>

      {/* Error Banner */}
      {(bannerError || isErrorData) && (
        <Alert severity="error" sx={{ mb: 3, borderRadius: 2 }}>
          {bannerError || "Error loading data"}
        </Alert>
      )}

      {/* Search Bar */}
      <Paper elevation={0} sx={{ p: 2, mb: 3, border: "1px solid #e0e0e0", borderRadius: 2 }}>
        <Stack direction={{ xs: "column", sm: "row" }} justifyContent="space-between" alignItems={{ xs: "stretch", sm: "center" }} spacing={2}>
          <TextField
            placeholder="Search by bucket name or access lists..."
            size="small"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            sx={{ minWidth: 300 }}
            InputProps={{
              startAdornment: <InputAdornment position="start"><SearchIcon color="action" /></InputAdornment>,
              endAdornment: searchTerm && (
                <InputAdornment position="end">
                  <IconButton size="small" onClick={() => setSearchTerm("")}><CloseIcon fontSize="small" /></IconButton>
                </InputAdornment>
              ),
            }}
          />
          <Chip label={`${filteredData.length} ${filteredData.length === 1 ? "record" : "records"}`} color="primary" variant="outlined" />
        </Stack>
      </Paper>

      {/* Table */}
      <TableContainer component={Paper} elevation={0} sx={{ border: "1px solid #e0e0e0", borderRadius: 2 }}>
        <Table>
          <TableHead>
            <TableRow sx={{ backgroundColor: "#f5f5f5" }}>
              <TableCell sx={{ fontWeight: 700, width: 180 }}>Bucket Name</TableCell>
              <TableCell sx={{ fontWeight: 700 }}>Access Lists</TableCell>
              {admin && <TableCell sx={{ fontWeight: 700, width: 120, textAlign: "center" }}>Actions</TableCell>}
            </TableRow>
          </TableHead>
          {isLoadingData ? (
            <TableSkeleton />
          ) : filteredData.length === 0 ? (
            <TableBody><EmptyState /></TableBody>
          ) : (
            <TableBody>
              {paginatedData.map((item) => (
                <TableRow
                  key={item.id}
                  sx={{
                    "&:hover": { backgroundColor: "#fafafa" },
                    "&:nth-of-type(even)": { backgroundColor: "#fcfcfc" },
                  }}
                >
                  <TableCell>
                    <Chip
                      icon={<StorageIcon sx={{ fontSize: 16 }} />}
                      label={item.bucket_name}
                      size="small"
                      sx={{ backgroundColor: "#e3f2fd", color: "#1565c0", fontWeight: 600 }}
                    />
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2" sx={{ whiteSpace: "pre-wrap", wordBreak: "break-word", lineHeight: 1.6 }}>
                      {item.access_lists}
                    </Typography>
                  </TableCell>
                  {admin && (
                    <TableCell sx={{ textAlign: "center" }}>
                      <Stack direction="row" spacing={0.5} justifyContent="center">
                        <Tooltip title="Edit">
                          <IconButton size="small" onClick={() => openEditDialog(item)} sx={{ color: "#1976d2" }}>
                            <EditIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Delete">
                          <IconButton size="small" onClick={() => requestDelete(item)} sx={{ color: "#d32f2f" }}>
                            <DeleteIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      </Stack>
                    </TableCell>
                  )}
                </TableRow>
              ))}
            </TableBody>
          )}
        </Table>
        {/* Table Pagination */}
        <Box sx={{ display: "flex", justifyContent: "flex-end", mt: 1 }}>
            <TablePagination
              component="div"
              count={filteredData.length}
              page={page}
              onPageChange={handleChangePage}
              rowsPerPage={rowsPerPage}
              onRowsPerPageChange={handleChangeRowsPerPage}
              rowsPerPageOptions={[5, 10, 25, 50]}
              showFirstButton
              showLastButton
              sx={{
                display: "flex",
                justifyContent: "flex-end",
                px: 2,
                "& .MuiTablePagination-toolbar": {
                  display: "flex",
                  alignItems: "center",
                  width: "100%",
                  flexWrap: "wrap",
                  gap: 1,
                  minHeight: 48,
                },
                "& .MuiTablePagination-selectLabel, & .MuiTablePagination-displayedRows": {
                  margin: 0,
                  alignSelf: "center",
                },
                "& .MuiTablePagination-actions": {
                  marginLeft: 1,
                },
              }}
            />
        </Box>
      </TableContainer>

      {/* Create/Edit Dialog */}
      <Dialog open={editDialog.open} onClose={closeDialog} maxWidth="sm" fullWidth PaperProps={{ sx: { borderRadius: 3 } }}>
        <DialogTitle sx={{ background: "linear-gradient(135deg, #496ab2 0%, #3f51b5 100%)", color: "white", py: 2 }}>
          <Stack direction="row" alignItems="center" spacing={1}>
            {editDialog.mode === "create" ? <AddCircleIcon /> : <EditIcon />}
            <Typography variant="h6" fontWeight={600}>
              {editDialog.mode === "create" ? "New Bucket Access List" : `Edit - ${editDialog.data?.bucket_name}`}
            </Typography>
          </Stack>
        </DialogTitle>
        <DialogContent sx={{ pt: 3, pb: 2 }}>
          <Stack spacing={3} sx={{ mt: 3 }}>
            <Autocomplete
              value={formValues.bucket_name || null}
              onChange={(event, newValue) => handleFormChange("bucket_name", newValue || "")}
              inputValue={formValues.bucket_name || ""}
              onInputChange={(event, newInputValue, reason) => {
                if (reason !== "reset") {
                  handleFormChange("bucket_name", newInputValue);
                }
              }}
              options={getAvailableBuckets()}
              freeSolo={false}
              disabled={editDialog.mode === "edit"}
              filterOptions={(options, { inputValue }) => {
                const filterValue = inputValue.toLowerCase();
                return options.filter((option) =>
                  option.toLowerCase().includes(filterValue)
                );
              }}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Bucket Name"
                  placeholder="Type to search buckets..."
                  error={!!validationErrors.bucket_name}
                  helperText={validationErrors.bucket_name || "Start typing to filter available buckets"}
                  required
                  InputProps={{
                    ...params.InputProps,
                    startAdornment: (
                      <>
                        <InputAdornment position="start">
                          <StorageIcon color="action" />
                        </InputAdornment>
                        {params.InputProps.startAdornment}
                      </>
                    ),
                  }}
                />
              )}
              renderOption={(props, option) => (
                <Box component="li" {...props} sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                  <StorageIcon sx={{ fontSize: 18, color: "#1565c0" }} />
                  <Typography variant="body2">{option}</Typography>
                </Box>
              )}
              noOptionsText={
                <Box sx={{ display: "flex", alignItems: "center", gap: 1, color: "text.secondary", py: 1 }}>
                  <ListAltIcon sx={{ fontSize: 20 }} />
                  <Typography variant="body2">No matching buckets found</Typography>
                </Box>
              }
              ListboxProps={{
                sx: {
                  maxHeight: 200,
                  "& .MuiAutocomplete-option": {
                    py: 1,
                    "&:hover": { backgroundColor: "#e3f2fd" },
                    "&[aria-selected='true']": { backgroundColor: "#bbdefb" },
                  },
                },
              }}
              sx={{
                "& .MuiOutlinedInput-root": {
                  "&:hover .MuiOutlinedInput-notchedOutline": { borderColor: "#496ab2" },
                  "&.Mui-focused .MuiOutlinedInput-notchedOutline": { borderColor: "#3f51b5" },
                },
              }}
            />
            <TextField
              label="Access Lists"
              value={formValues.access_lists}
              onChange={(e) => handleFormChange("access_lists", e.target.value)}
              error={!!validationErrors.access_lists}
              helperText={validationErrors.access_lists || "Enter access lists separated by commas"}
              fullWidth
              required
              placeholder="Enter access lists (comma separated)..."
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <ListAltIcon color="action" />
                  </InputAdornment>
                ),
              }}
            />
          </Stack>
        </DialogContent>
        <Divider />
        <DialogActions sx={{ p: 2, gap: 1 }}>
          <Button variant="outline-secondary" onClick={closeDialog}>Cancel</Button>
          <Button variant="primary" onClick={handleSave} disabled={isPendingData} style={{ display: "flex", alignItems: "center", gap: 6 }}>
            <SaveIcon fontSize="small" />
            {isPendingData ? "Saving..." : "Save"}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete Dialog */}
      <Dialog open={!!deleteTargetRow} onClose={handleCancelDelete} maxWidth="xs" fullWidth>
        <DialogContent>
          <DeleteConfirmation
            title={<>Are you sure you want to delete Bucket Access List - <strong>{deleteTargetRow?.bucket_name ?? ""}</strong>?</>}
            onConfirm={handleConfirmDelete}
            onCancel={handleCancelDelete}
          />
        </DialogContent>
      </Dialog>

      {/* Toast */}
      <Snackbar open={toast.open} autoHideDuration={4000} onClose={closeToast} anchorOrigin={{ vertical: "top", horizontal: "center" }}>
        <Alert onClose={closeToast} severity={toast.severity} variant="filled" sx={{ width: "100%", borderRadius: 2 }}>
          {toast.message}
        </Alert>
      </Snackbar>
    </Box>
  );
}